/**
 * @React
 */
import React, { useEffect, useState, useCallback } from "react";
/**
 * @Router
 */

import { useHistory, useLocation, useParams } from "react-router-dom";
/**
 * @Mui
 */
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  DialogActions,
  Button,
  DialogTitle,
  FormLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  CardActions,
  CardHeader,
  Avatar,
  IconButton,
  Divider,

} from "@mui/material";
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DoneOutlineOutlinedIcon from '@mui/icons-material/DoneOutlineOutlined';
import DownloadDoneOutlinedIcon from '@mui/icons-material/DownloadDoneOutlined';
import FileDownloadDoneOutlinedIcon from '@mui/icons-material/FileDownloadDoneOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import axiosInstance from "utilities/axios";
import { generateUrl } from "utilities/generateRequestUrl";
import config from "utilities/generateRequestUrl/config";
/**
 * @Context
 */
import {
  useMonitoringApplicationContext,
  ApplicationPreview,
  Type
} from "context/MonitoringApplicationContext";
import { useAuth } from "context/AuthContext";
import { useMonitoringFormContext, useGeneralInfoContext } from "context";
/**
 * @Components
 */
import Switch from "components/CustomComponents/SwitchComponent";
import Pagination from "components/common/Pagination";
import TableSkeleton from "components/TableSkeleton";
import { FilterGrid, FilterTextField, FilterSelect, FilterMenuItem, AssignmentMultiSelect } from "components/common/FilterControls";
import CustomTooltip from "components/CustomComponents/CustomTooltip";

import openInNewTab from "utilities/newTab";
import { ProgramComponent } from "types";
import useAccess from "hooks/useAccess";

interface MonitoringDashboardProps {
  tab: Type;
}

type AssignmentOption = { _id: string; application_type: string; assigned_at: string; ended_at: string | null; is_active: boolean; organization: { name: string } };

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({
  tab
}) => {
  const {
    applications,
    fetchApplications,
    filter,
    setFilter,
    totalPages,
    loading
  } = useMonitoringApplicationContext();

  const { canSeeAllApps, canReviewApp, isMonitoring } = useAccess();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [assignments, setAssignments] = useState<AssignmentOption[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (canSeeAllApps) {
      axiosInstance.get(generateUrl(config.client, "getAssignments", null, { application_type: "universal" }))
        .then((res) => {
          setAssignments(res.data);
          const activeIds = (res.data as AssignmentOption[])
            .filter((a) => a.is_active)
            .map((a) => a._id);
          if (activeIds.length > 0) {
            setFilter((prev: any) => ({ ...prev, monitoring_assignment: activeIds }));
          }
        })
        .catch(() => {});
    }
  }, [authLoading, canSeeAllApps]);
  const history = useHistory();
  type Column = {
    id: keyof ApplicationPreview;
    label: string;
  };

  const {
    setAgreement,
    setSelectedCompany,
    setSelectedContract,
  } = useMonitoringFormContext();
  let {
    options: GeneralInfo,
    setOptions: setGeneralInfoOptions,
    generatedValues: GeneralInforValues,
    setGeneratedValues: setGeneralInfoValues,
    data: GeneralInfoOptions,
    load: GeneralInfoLoad,
    error: GeneralInfoError,
  } = useGeneralInfoContext();
  const propertyMapping = {
    actual_municipality: "municipalities",
  }

  const columns: Column[] = [
    { id: "contract_number", label: "ხელშეკრულების ნომერი/დამტკიცებული სესხის/ლიზინგის ID" },
    { id: "company_name", label: "ბენეფიციარის დასახელება" },
    { id: "company_identification_code", label: "ბენეფიციარის ს/კ" },
    { id: "approved_amount", label: "დამტკიცებული თანხა - ₾" },
    { id: "contract_signed_date", label: "ხელშეკრულების გაფაორმების თარიღი" },
    { id: "actual_municipality", label: "საქმიანობის განხორციელების ფაქტობრივი მისამართი - მუნიციპალიტეტი" },
    { id: "program_component", label: "პროგრამის კომპონენტი" },
    ...(tab !== Type.agreement ? [{ id: "created_by", label: "მომხმარებელი" }] as Column[] : []),
  ];

  useEffect(() => {
    setFilter((prev) => ({ ...prev, type: tab, page: 1 }));
  }, [tab]);

  function redirectToDetailsOrCreate(url: string, callback: (a: string) => void) {
    if (callback) callback(url);
  }

  const getAgreement = (
    company_identification_code: string,
    contract_number: string) => {
    setSelectedCompany(company_identification_code);
    setSelectedContract(contract_number);
    // setAgreement(null);
    // window.location.reload();
    return history.push(`/monitoring-form`);
  };
  return (
    <>
      <Card sx={{ width: "100%" }}>
        <TabContext value={tab}>
          <Box sx={{ padding: 2 }}>
            <FilterGrid>
              <FilterTextField
                label="ბენეფიციარის დასახელება"
                value={filter.company_name}
                onChange={(e) => setFilter({ ...filter, company_name: e.target.value })}
              />
              <FilterTextField
                label="ბენეფიციარის ს/კ"
                value={filter.company_identification_code}
                onChange={(e) => setFilter({ ...filter, company_identification_code: e.target.value })}
              />
              <FilterSelect
                label="მუნიციპალიტეტი"
                labelId="municipality"
                value={filter.municipality}
                onChange={(e) => setFilter({ ...filter, municipality: e.target.value as string })}
              >
                <FilterMenuItem value=""><em>ყველა</em></FilterMenuItem>
                {GeneralInfo?.municipalities && Object.keys(GeneralInfo?.municipalities).map((key) => (
                  <FilterMenuItem key={key} value={key}>{GeneralInfo?.municipalities[key].label}</FilterMenuItem>
                ))}
              </FilterSelect>
              <FilterSelect
                label="კომპონენტი"
                labelId="component"
                value={filter.component}
                onChange={(e) => setFilter({ ...filter, component: e.target.value as string })}
              >
                <FilterMenuItem value=""><em>ყველა</em></FilterMenuItem>
                {Object.keys(ProgramComponent).map((key) => {
                  const enumKey = key as keyof typeof ProgramComponent;
                  return <FilterMenuItem key={key} value={ProgramComponent[enumKey]}>{ProgramComponent[enumKey]}</FilterMenuItem>;
                })}
              </FilterSelect>
              {canSeeAllApps && (
                <AssignmentMultiSelect
                  value={filter.monitoring_assignment || []}
                  onChange={(val) => setFilter((prev: any) => ({ ...prev, monitoring_assignment: val }))}
                  assignments={assignments}
                />
              )}
            </FilterGrid>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
              <Button variant="contained" color="primary" onClick={fetchApplications}>
                ძებნა
              </Button>
            </Box>
          </Box>
          <Divider />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {
                      columns.map((column) => (
                        <TableCell key={column.id}>{column.label}</TableCell>
                      ))
                    }
                    <TableCell key={1001}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    applications.map((application, index) => {
                      let returned = false;
                      if (tab === Type.monitoring) {
                        if (
                          application.monitoring_approval_history
                          &&
                          application.monitoring_approval_history.length > 0
                        ) {
                          let lastApproval = application.monitoring_approval_history[application.monitoring_approval_history.length - 1]?.approved;
                          returned = lastApproval === false ? true : false;
                        }
                      }

                      return (
                        <TableRow
                          sx={{
                            backgroundColor: returned ? "#F57C00" : "inherits",
                          }}
                          key={application._id}
                          onDoubleClick={() => redirectToDetailsOrCreate(`${tab === Type.monitoring || tab === Type.approved ? '/monitoring-details' : '/monitoring-agreement-details'}/${application._id}`, openInNewTab)}>
                          {
                            columns.map((column) => {
                              let value;
                              if (column.id === "actual_municipality") {
                                if (
                                  GeneralInforValues
                                  &&
                                  GeneralInforValues["municipalities"]
                                  && application[column.id]) {
                                  value = GeneralInforValues["municipalities"][application[column.id] as keyof typeof GeneralInforValues["municipalities"]].municipality;
                                }
                              } else if (application[column.id as keyof ApplicationPreview] && column.id === "created_by") {
                                value = application[column.id]?.name || "";
                              }
                              else {
                                value = application[column.id as keyof ApplicationPreview];
                              }
                              return (
                                <TableCell
                                  sx={{
                                    color: returned ? "#fff" : "inherits",
                                  }}
                                  key={column.id}>
                                  {returned ? <b>{value || ""}</b> : <>{value || ""}</>}
                                </TableCell>
                              )
                            })}
                          <TableCell
                            key={`${application._id} - ${index}`}
                            align="left"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                width: "100px",
                              }}
                            >
                              {
                                !application?.monitoring_approval && (tab !== Type.monitoring || (returned && canReviewApp))
                                  ?
                                  <CustomTooltip title={tab === Type.agreement ? "მონიტORINGი" : "რედაქტირება"} placement="bottom-end" arrow>
                                    <IconButton
                                      aria-label="edit"
                                      size="small"
                                      onClick={() => tab === Type.monitoring
                                        ?
                                        redirectToDetailsOrCreate(`/monitoring-update-form/${application._id}`, history.push) : getAgreement(application.company_identification_code, application.contract_number)}>
                                      {
                                        tab === Type.monitoring ?
                                          <>
                                            <EditOutlinedIcon
                                              sx={{
                                                fontSize: "1rem",
                                                color: returned ? "#fff" : "#56B9E9",
                                              }}
                                            />

                                          </>
                                          :
                                          <AddCircleOutlinedIcon
                                            sx={{
                                              fontSize: "1rem",
                                              color: "#56B9E9",
                                            }}
                                          />
                                      }
                                    </IconButton>
                                  </CustomTooltip>
                                  :
                                  null
                              }
                              {
                                [Type.monitoring, Type.approved].includes(tab)
                                &&
                                application.monitoring_approval
                                &&
                                <CustomTooltip title="დადასტურებული" placement="bottom-end" arrow>
                                  <IconButton
                                    aria-label="detail"
                                    size="small"
                                    sx={{ fontSize: "1em" }}
                                  >
                                    <DownloadDoneOutlinedIcon
                                      sx={{
                                        color: "#00695f",
                                        fontSize: "1.1rem"
                                      }}
                                    />
                                  </IconButton>
                                </CustomTooltip>
                              }
                              <CustomTooltip
                                title={tab === Type.agreement ? "დასამონიტორინგებელი პროექტის დეტალები" : `${tab === Type.monitoring ? 'დამონიტორინგებული' : 'დადასტურებული'} პროექტის დეტალები`}
                                placement="bottom-end"
                                arrow
                              >
                                <IconButton
                                  aria-label="detail"
                                  size="small"
                                  sx={{ fontSize: "1em" }}
                                  onClick={() => redirectToDetailsOrCreate(`${([Type.monitoring, Type.approved].includes(tab)) ? '/monitoring-details/' : '/monitoring-agreement-details/'}${application._id}`, openInNewTab)}
                                >
                                  <InfoOutlinedIcon fontSize="inherit" sx={{ color: returned ? "#fff" : "#56B9E9" }} />
                                </IconButton>
                              </CustomTooltip>
                              {
                                [Type.monitoring, Type.approved].includes(tab)
                                  &&
                                  application.contract
                                  ?
                                  <CustomTooltip title="სააგენტოში დადასტურების ეტაპზე არსებული დეტალური ინფორმაცია 11" placement="bottom-end" arrow>
                                    <IconButton
                                      aria-label="detail"
                                      size="small"
                                      sx={{ fontSize: "1em" }}
                                      onClick={() => redirectToDetailsOrCreate(`/monitoring-agreement-details/${application.contract}`, openInNewTab)}
                                    >
                                      <BackupTableOutlinedIcon fontSize="inherit" sx={{ color: returned ? "#fff" : "#56B9E9" }} />
                                    </IconButton>
                                  </CustomTooltip>
                                  :
                                  null
                              }
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  }
                  {
                    loading
                    &&
                    <TableSkeleton
                      amount={5}
                      columns={columns}
                    />}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={totalPages}
              page={filter.page || 1}
              onChange={(event, value) => setFilter({ ...filter, page: value })}
              rowsPerPage={filter.amountOnPage || 15}
              onRowsPerPageChange={(event) => setFilter({ ...filter, amountOnPage: event.target.value as number, page: 1 })}
            />
          </CardContent>
        </TabContext>
      </Card >
    </>
  );
};

export default MonitoringDashboard;
